import { postGrowthEngineAnalytics } from './growthEngineApi'
import { OLLIE_PLATFORM } from './ollieFindingsApi'

export const OLLIE_CHAT_OPENED = 'ollie_chat_opened'
export const OLLIE_CHAT_CLOSED = 'ollie_chat_closed'
export const OLLIE_CHAT_RETURNED = 'ollie_chat_returned'

/** Counts for close-session metadata (2.3 abandonment stages). */
export function ollieSessionMessageCounts(messages = []) {
  let userMessageCount = 0
  let assistantMessageCount = 0
  let hadSuccessfulReply = false
  for (const message of messages || []) {
    if (message?.role === 'user') userMessageCount += 1
    if (message?.role === 'assistant') {
      assistantMessageCount += 1
      if (!message.timedOut && !message.needsEscalation) {
        hadSuccessfulReply = true
      }
    }
  }
  return { userMessageCount, assistantMessageCount, hadSuccessfulReply }
}

const PRIOR_CHAT_KEY = venueId => `ollie:hadPriorChat:${venueId}`

const postLifecycle = (venueId, body) => {
  if (!venueId || !body?.eventName) return Promise.resolve(null)
  return postGrowthEngineAnalytics(venueId, {
    platform: body.platform || OLLIE_PLATFORM,
    ...body,
  }).catch(() => null)
}

const readHadPriorChat = venueId => {
  if (!venueId || typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem(PRIOR_CHAT_KEY(venueId)) === '1'
  } catch {
    return false
  }
}

const markHadPriorChat = venueId => {
  if (!venueId || typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(PRIOR_CHAT_KEY(venueId), '1')
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Panel/screen open — Activation 2.3 session lifecycle.
 * Also fires returned when this venue had a prior messaged session.
 */
export function trackOllieChatOpened({
  venueId,
  conversationId = null,
  entryPoint = null,
  userMessageCount = 0,
  platform = OLLIE_PLATFORM,
} = {}) {
  if (!venueId) return Promise.resolve(null)
  const metadata = {
    source: 'ollie',
    surface: 'chat',
    ...(conversationId ? { conversationId: String(conversationId) } : {}),
    ...(entryPoint ? { entryPoint: String(entryPoint) } : {}),
    userMessageCount: Number(userMessageCount) || 0,
  }
  const opened = postLifecycle(venueId, {
    eventName: OLLIE_CHAT_OPENED,
    platform,
    metadata,
  })
  if (readHadPriorChat(venueId)) {
    postLifecycle(venueId, {
      eventName: OLLIE_CHAT_RETURNED,
      platform,
      metadata,
    })
  }
  return opened
}

/**
 * Panel/screen close. userMessageCount === 0 → opened-no-message stage.
 * Marks venue for returned-later when they had sent at least one message.
 */
export function trackOllieChatClosed({
  venueId,
  conversationId = null,
  userMessageCount = 0,
  assistantMessageCount = 0,
  hadSuccessfulReply = false,
  platform = OLLIE_PLATFORM,
} = {}) {
  if (!venueId) return Promise.resolve(null)
  const count = Number(userMessageCount) || 0
  if (count > 0) markHadPriorChat(venueId)
  return postLifecycle(venueId, {
    eventName: OLLIE_CHAT_CLOSED,
    platform,
    metadata: {
      source: 'ollie',
      surface: 'chat',
      ...(conversationId ? { conversationId: String(conversationId) } : {}),
      userMessageCount: count,
      assistantMessageCount: Number(assistantMessageCount) || 0,
      hadSuccessfulReply: Boolean(hadSuccessfulReply),
    },
  })
}

export function resetOllieConversationLifecycleForTests() {
  if (typeof localStorage === 'undefined') return
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith('ollie:hadPriorChat:'))
      .forEach(key => localStorage.removeItem(key))
  } catch {
    // ignore
  }
}
